// The Phase 0 editor spike, kept as the iPad test rig for the editor's
// mechanics. Same editor and store as the real document screen; the document
// lives in this browser and nothing is sent anywhere.

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";

import { Editor } from "../editor/Editor";
import { splitMarkdown } from "../markdown/split";
import { DocumentStore } from "../store/documentStore";
import { type DocState, type ParagraphId, docFromParagraphs } from "../store/ops";
import { APP_NAME } from "../ui/brand";
import { useKeyboardInset } from "./useKeyboardInset";

const STORAGE_KEY = "metis.spike.doc.v2";

const SAMPLE = `# Editor spike

This page tests the four things the plan says must hold on an iPad before anything else is built.

Tap into this paragraph and press Return in the middle of a sentence. The keyboard should stay up and the second half should become its own paragraph.

Put the cursor at the very start of this paragraph and press Backspace. It should merge into the one above.

- Tap a number in the left gutter to select a paragraph.
- Drag across the numbers to select a range.
- Hold a number for half a second and it lifts. Drag it and let go to reorder.

\`\`\`
Inside a code fence, Return is a newline
until the closing fence is typed.
\`\`\`

Arrow up from a one-line paragraph, or from the start of a longer one, moves to the paragraph above. Arrow down does the same from the end. Undo is Cmd+Z, or the keyboard's undo key.`;

function load(): DocState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DocState;
      if (Array.isArray(parsed.order) && parsed.order.length > 0 && parsed.text) return parsed;
    }
  } catch {
    // fall through to the sample
  }
  return docFromParagraphs(splitMarkdown(SAMPLE), "Editor spike");
}

export function SpikePage() {
  const [store] = useState(() => new DocumentStore(load(), () => {}));
  const doc = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const [selection, setSelection] = useState<ParagraphId[]>([]);
  const keyboardInset = useKeyboardInset();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(doc));
    } catch {
      // storage can be unavailable; the spike does not depend on it
    }
  }, [doc]);

  const liveSelection = useMemo(() => selection.filter((id) => id in doc.text), [selection, doc]);

  return (
    <div className="screen" style={{ paddingBottom: `calc(96px + ${keyboardInset}px)` }}>
      <header className="topbar">
        <Link to="/" className="text-button back" aria-label="Back to library">
          ‹
        </Link>
        <span className="wordmark">{APP_NAME}</span>
        <span className="title">Editor spike</span>
        <button
          type="button"
          className="text-button"
          onClick={() => {
            setSelection([]);
            store.reset(docFromParagraphs(splitMarkdown(SAMPLE), "Editor spike"));
          }}
        >
          Reset
        </button>
      </header>
      <main className="body">
        <Editor doc={doc} store={store} selection={liveSelection} onSelectionChange={setSelection} />
      </main>
      <footer className="bottom" style={{ bottom: keyboardInset }}>
        <div className="strip">Local only · nothing is sent anywhere</div>
        <div className="bar">
          <button type="button" className="ask" disabled>
            {liveSelection.length > 0 ? "Ask about this" : "Review the whole thing"}
          </button>
          <span className="bar-hint">
            {liveSelection.length > 0
              ? `${liveSelection.length} paragraph${liveSelection.length === 1 ? "" : "s"} selected`
              : "Nothing selected"}
          </span>
        </div>
      </footer>
    </div>
  );
}
